import React from 'react';
import '../components/grammar-page.css';
import { FaBookOpen, FaLightbulb } from 'react-icons/fa';

const MustHaveToExplanation = () => {
  return (
    <div className="grammar-explanation">
      <h2><FaBookOpen /> Must / Have to / Need to / Should</h2>

      <p>
        We use different modal and semi-modal verbs to express <strong>obligation</strong>, <strong>necessity</strong>, <strong>lack of obligation</strong>, and <strong>prohibition</strong>. The choice depends on how strong the obligation is and whether it comes from an external rule or the speaker's opinion.
      </p>

      <div className="grammar-explanation-grid">
        <div className="example-block">
          <h3><FaLightbulb /> Strong Obligation (External Rules)</h3>
          <ul>
            <li><strong>Must</strong> = strong obligation, often from the speaker or official rules<br /><em>You <strong>must</strong> wear a seatbelt. Passengers <strong>must</strong> show ID.</em></li>
            <li><strong>Have to</strong> = strong obligation from external circumstances/laws<br /><em>I <strong>have to</strong> submit the report by Friday. In the UK, you <strong>have to</strong> drive on the left.</em></li>
            <li><strong>Need to</strong> = necessity, often practical or personal<br /><em>You <strong>need to</strong> update your password. We <strong>need to</strong> leave early.</em></li>
          </ul>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> Mild Obligation / Advice</h3>
          <ul>
            <li><strong>Should</strong> = advice, recommendation, or mild obligation<br /><em>You <strong>should</strong> see a doctor. We <strong>should</strong> arrive on time.</em></li>
            <li><strong>Ought to</strong> = similar to <strong>should</strong>, slightly more formal<br /><em>You <strong>ought to</strong> check the details.</em></li>
          </ul>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> No Obligation</h3>
          <ul>
            <li><strong>Don't have to</strong> = not necessary, no obligation<br /><em>You <strong>don't have to</strong> come if you're busy. Students <strong>don't have to</strong> wear uniform on Fridays.</em></li>
            <li><strong>Needn't / Don't need to</strong> = not necessary (slightly more formal)<br /><em>You <strong>needn't</strong> worry. We <strong>don't need to</strong> print the tickets.</em></li>
          </ul>
        </div>

        <div className="example-block">
          <h3><FaLightbulb /> Prohibition (Must Not Do)</h3>
          <ul>
            <li><strong>Mustn't</strong> = strong prohibition, forbidden<br /><em>You <strong>mustn't</strong> smoke here. Visitors <strong>mustn't</strong> enter without a badge.</em></li>
            <li><strong>Can't</strong> = not allowed, prohibition (less formal)<br /><em>You <strong>can't</strong> park here. We <strong>can't</strong> use phones during the exam.</em></li>
            <li><strong>Shouldn't</strong> = not recommended, mild prohibition<br /><em>You <strong>shouldn't</strong> eat so much sugar. Employees <strong>shouldn't</strong> share passwords.</em></li>
          </ul>
        </div>
      </div>

      <div className="grammar-flex-table">
        {[
          ['Meaning', 'Verb', 'Example', 'Strength'],
          ['Strong obligation (speaker)', 'must', 'You must sign here.', '★★★'],
          ['Strong obligation (external)', 'have to', 'I have to pay taxes.', '★★★'],
          ['Necessity', 'need to', 'You need to rest.', '★★☆'],
          ['Advice / mild obligation', 'should', 'You should sleep more.', '★☆☆'],
          ['No obligation', "don't have to", "You don't have to wait.", '—'],
          ['Prohibition', "mustn't / can't", "You mustn't enter.", '★★★'],
          ['Not recommended', "shouldn't", "You shouldn't skip breakfast.", '★☆☆'],
        ].map((row, index) => (
          <div key={index} className={`grammar-flex-row${index === 0 ? ' header' : ''}`}>
            {row.map((cell, i) => (
              <div key={i} className="grammar-cell">{cell}</div>
            ))}
          </div>
        ))}
      </div>

      <div className="grammar-explanation-text">
        <h3><FaLightbulb /> Key Differences</h3>
        <ul>
          <li><strong>Must vs Have to:</strong> <em>Must</em> often reflects the speaker's authority or opinion; <em>have to</em> reflects external rules. <em>I must exercise</em> (my decision) vs <em>I have to exercise</em> (doctor's orders).</li>
          <li><strong>Don't have to vs Mustn't:</strong> <em>Don't have to</em> = no obligation (you can if you want); <em>mustn't</em> = forbidden (you cannot).</li>
          <li><strong>Needn't / Don't need to:</strong> Used for present/future absence of necessity. <em>Needn't</em> is more common in British English.</li>
          <li><strong>Past forms:</strong> <em>had to</em> (past of must/have to), <em>didn't have to</em> (no obligation in past), <em>needn't have done</em> (unnecessary action that was done).</li>
        </ul>
        <p><strong>Examples:</strong></p>
        <ul>
          <li>You <strong>must</strong> be 18 to vote. (law)</li>
          <li>I <strong>have to</strong> finish this report tonight. (deadline)</li>
          <li>You <strong>don't have to</strong> bring lunch; it's provided.</li>
          <li>You <strong>mustn't</strong> use your phone in the cinema.</li>
          <li>You <strong>should</strong> study for the test.</li>
          <li>We <strong>need to</strong> buy milk.</li>
        </ul>
      </div>
    </div>
  );
};

export default MustHaveToExplanation;